import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UserService from '../services/UserService';
import { initParcoursFromDrumYears, preUnlockContentFromDrumYears } from '../utils/parcoursProgressionStorage';
import { functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import MonitoringService from '../services/MonitoringService';

const activateCyrilNotationCF = httpsCallable(functions, 'activateCyrilNotation');
const promoteToTeacherCF = httpsCallable(functions, 'promoteToTeacher');

const FirstLoginPage = () => {
  const [pseudo, setPseudo] = useState('');
  const [age, setAge] = useState('');
  const [drumYears, setDrumYears] = useState('');
  const [profCode, setProfCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestedPseudo, setSuggestedPseudo] = useState('');
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [pendingAccountType, setPendingAccountType] = useState(
    new URLSearchParams(window.location.search).get('role') === 'teacher' ? 'teacher' : 'email'
  );
  const { createProfile, currentUser, loadUserProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Lire le type de compte depuis le pendingProfile_ en localStorage
  React.useEffect(() => {
    if (currentUser?.uid) {
      const key = `pendingProfile_${currentUser.uid}`;
      const pendingRaw = localStorage.getItem(key);
      if (pendingRaw) {
        try {
          const { accountType, teacherCode: storedTeacherCode } = JSON.parse(pendingRaw);
          if (accountType) setPendingAccountType(accountType);
          // Restaurer le code prof en sessionStorage si perdu (lien email ouvert dans un nouvel onglet)
          if (storedTeacherCode && !sessionStorage.getItem('pendingTeacherAccessCode')) {
            sessionStorage.setItem('pendingTeacherAccessCode', storedTeacherCode);
          }
        } catch (e) {
          console.error('[FirstLoginPage] Erreur parsing pendingProfile_:', e);
        }
      }
    }
  }, [currentUser]);

  // isTeacher : paramètre URL OU accountType stocké en localStorage
  const isTeacher = new URLSearchParams(location.search).get('role') === 'teacher'
    || pendingAccountType === 'teacher';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowSuggestion(false);

    // Validation du pseudo
    if (pseudo.trim().length < 3) {
      setError('Le pseudo doit contenir au moins 3 caractères');
      return;
    }

    if (pseudo.trim().length > 20) {
      setError('Le pseudo ne peut pas dépasser 20 caractères');
      return;
    }

    // Vérifier que le pseudo ne contient que des caractères autorisés
    const validPseudoRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validPseudoRegex.test(pseudo.trim())) {
      setError('Le pseudo ne peut contenir que des lettres, chiffres, tirets et underscores');
      return;
    }

    // Validation de l'âge (seulement pour les élèves)
    let ageNum = 0;
    let drumYearsNum = 0;

    if (!isTeacher) {
      ageNum = parseInt(age);
      if (!age || ageNum < 5 || ageNum > 120) {
        setError('Veuillez entrer un âge valide (entre 5 et 120 ans)');
        return;
      }

      // Validation des années de batterie (seulement pour les élèves)
      drumYearsNum = parseInt(drumYears);
      if (!drumYears || ![1, 2, 3, 4, 5].includes(drumYearsNum)) {
        setError('Veuillez sélectionner vos années de pratique');
        return;
      }
    }

    setLoading(true);

    try {
      // Vérifier si le pseudo est déjà pris
      const isPseudoTaken = await UserService.isPseudoTaken(pseudo.trim());

      if (isPseudoTaken) {
        // Générer un pseudo unique
        const uniquePseudo = await UserService.generateUniquePseudo(pseudo.trim());
        setSuggestedPseudo(uniquePseudo);
        setShowSuggestion(true);
        setError(`Le pseudo "${pseudo}" est déjà pris.`);
        setLoading(false);
        return;
      }

      // Créer le profil avec le pseudo, âge et drumYears
      await createProfile(pseudo.trim(), ageNum, drumYearsNum, pendingAccountType);
      localStorage.removeItem(`pendingProfile_${currentUser.uid}`);

      // Appliquer la nomenclature Cyril si le code prof correspond (validation côté serveur)
      if (!isTeacher && profCode.trim()) {
        await activateCyrilNotationCF({ code: profCode.trim() }).catch(() => {});
      }

      // Rattacher l'élève à son professeur si un code PROF-... est fourni
      if (!isTeacher && profCode.trim().startsWith('PROF-')) {
        await UserService.linkStudentToTeacher(currentUser.uid, profCode.trim()).catch(err => {
          console.warn('[FirstLoginPage] Rattachement prof échoué:', err.message);
          MonitoringService.error('onboarding_teacher_link_failed', { errorMessage: err.message }, currentUser.uid);
        });
      }

      // Positionner le parcours à le chapitre correspondant au niveau
      if (!isTeacher && drumYearsNum > 0) {
        await initParcoursFromDrumYears(drumYearsNum);
        preUnlockContentFromDrumYears(drumYearsNum, currentUser.uid).catch(err => {
          console.warn('[Parcours] Erreur pré-déblocage contenu:', err);
        });
      }

      if (isTeacher) {
        // Attendre que le profil soit bien créé avant de promouvoir
        await new Promise(resolve => setTimeout(resolve, 1000));

        try {
          const pendingAccessCode = sessionStorage.getItem('pendingTeacherAccessCode') || null;
          const result = await promoteToTeacherCF({ accessCode: pendingAccessCode });
          const teacherCode = result.data.teacherCode;
          sessionStorage.removeItem('pendingTeacherAccessCode');

          // Forcer un rechargement du profil dans AuthContext
          await loadUserProfile(currentUser.uid);

          alert(`Compte professeur créé avec succès ! Votre code: ${teacherCode}\nVous allez être redirigé vers l'accueil.`);
        } catch (teacherError) {
          console.error('❌ Erreur lors de la promotion en professeur:', teacherError);
          setError('Le profil a été créé mais une erreur est survenue lors de la configuration du compte professeur.');
        }
      }

      navigate('/'); // Rediriger vers l'accueil après création du profil
    } catch (error) {
      console.error('❌ Erreur création profil:', error);
      setError('Erreur lors de la création du profil. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptSuggestion = async () => {
    setError('');
    setShowSuggestion(false);
    setLoading(true);

    try {
      // Pour les professeurs, utiliser des valeurs par défaut
      const ageNum = isTeacher ? 0 : parseInt(age);
      const drumYearsNum = isTeacher ? 0 : parseInt(drumYears);

      await createProfile(suggestedPseudo, ageNum, drumYearsNum, pendingAccountType);
      localStorage.removeItem(`pendingProfile_${currentUser.uid}`);

      // Appliquer la nomenclature Cyril si le code prof correspond (validation côté serveur)
      if (!isTeacher && profCode.trim()) {
        await activateCyrilNotationCF({ code: profCode.trim() }).catch(() => {});
      }

      // Rattacher l'élève à son professeur si un code PROF-... est fourni
      if (!isTeacher && profCode.trim().startsWith('PROF-')) {
        await UserService.linkStudentToTeacher(currentUser.uid, profCode.trim()).catch(err => {
          console.warn('[FirstLoginPage] Rattachement prof échoué:', err.message);
          MonitoringService.error('onboarding_teacher_link_failed', { errorMessage: err.message }, currentUser.uid);
        });
      }

      // Positionner le parcours à le chapitre correspondant au niveau
      if (!isTeacher && drumYearsNum > 0) {
        await initParcoursFromDrumYears(drumYearsNum);
        preUnlockContentFromDrumYears(drumYearsNum, currentUser.uid).catch(err => {
          console.warn('[Parcours] Erreur pré-déblocage contenu:', err);
        });
      }

      // Si professeur, promouvoir et générer code
      if (isTeacher) {
        if (!currentUser) {
          console.error('❌ currentUser non disponible pour promouvoir en professeur');
          setError('Erreur: utilisateur non connecté. Veuillez réessayer.');
          setLoading(false);
          return;
        }

        // Attendre que le profil soit bien créé avant de promouvoir
        await new Promise(resolve => setTimeout(resolve, 1000)); // Augmenté à 1 seconde

        try {
          const pendingAccessCode = sessionStorage.getItem('pendingTeacherAccessCode') || null;
          const result = await promoteToTeacherCF({ accessCode: pendingAccessCode });
          const teacherCode = result.data.teacherCode;
          sessionStorage.removeItem('pendingTeacherAccessCode');

          await loadUserProfile(currentUser.uid);

          alert(`Compte professeur créé avec succès ! Votre code: ${teacherCode}\nVous allez être redirigé vers l'accueil.`);
        } catch (teacherError) {
          console.error('❌ Erreur lors de la promotion en professeur:', teacherError);
          setError('Le profil a été créé mais une erreur est survenue lors de la configuration du compte professeur.');
        }
      }

      navigate('/');
    } catch (error) {
      console.error('❌ Erreur création profil:', error);
      setError('Erreur lors de la création du profil. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden py-4">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-transparent to-slate-950/50 pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-amber-500/10 to-transparent pointer-events-none" />
      <div className="bg-slate-800/60 backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-2xl w-full max-w-md relative z-10 max-h-full overflow-y-auto">
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold text-amber-50 font-display mb-1">Bienvenue sur Drumigo !</h1>
          <p className="text-slate-400 text-sm">
            {isTeacher ? 'Créez votre profil professeur' : 'Choisissez votre pseudo'}
          </p>
        </div>

        {!isTeacher && (
          <div className="bg-blue-900/30 border border-blue-500/30 text-blue-300 px-3 py-2 rounded-xl mb-3">
            <p className="font-semibold text-blue-200 text-sm">Première connexion</p>
            <p className="text-xs">Ces informations seront utilisées pour le classement</p>
          </div>
        )}

        {/* Indicateur professeur */}
        {isTeacher && (
          <div className="mb-3">
            <div className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-3">
              <p className="text-amber-200 font-semibold flex items-center gap-2 text-sm">
                <span className="text-xl">👨‍🏫</span>
                Création d'un compte professeur
              </p>
              <p className="text-xs text-amber-300/70 mt-1">
                Un code unique sera généré pour que vos élèves puissent se lier à vous
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-900/40 border border-red-500/50 text-red-300 px-3 py-2 rounded-xl mb-3 text-sm">
            {error}
          </div>
        )}

        {showSuggestion && (
          <div className="bg-amber-900/30 border border-amber-500/40 text-amber-200 px-3 py-2 rounded-xl mb-3 text-sm">
            <p className="font-semibold mb-2">Nous te proposons : {suggestedPseudo}</p>
            <div className="flex gap-2">
              <button
                onClick={handleAcceptSuggestion}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-3 py-2 rounded-full transition-all disabled:opacity-50"
              >
                Accepter
              </button>
              <button
                onClick={() => setShowSuggestion(false)}
                className="flex-1 bg-slate-700 hover:bg-slate-600 border border-white/10 text-amber-50 px-3 py-2 rounded-full transition-colors"
              >
                Choisir un autre
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="pseudo" className="block text-slate-300 font-semibold mb-1 text-sm">
              Pseudo *
            </label>
            <input
              type="text"
              id="pseudo"
              value={pseudo}
              onChange={(e) => setPseudo(e.target.value)}
              className="w-full px-4 py-2 bg-slate-900/60 border border-white/10 rounded-xl text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm"
              placeholder="MonPseudo123 (3-20 car., lettres/chiffres/_-)"
              required
              disabled={loading}
              minLength={3}
              maxLength={20}
            />
          </div>

          {/* Champs pour les élèves uniquement */}
          {!isTeacher && (
            <>
              <div className="mb-3">
                <label htmlFor="age" className="block text-slate-300 font-semibold mb-1 text-sm">
                  Âge *
                </label>
                <input
                  type="number"
                  id="age"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900/60 border border-white/10 rounded-xl text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm"
                  placeholder="15"
                  required
                  disabled={loading}
                  min={5}
                  max={120}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="drumYears" className="block text-slate-300 font-semibold mb-1 text-sm">
                  Niveau de batterie *
                </label>
                <select
                  id="drumYears"
                  value={drumYears}
                  onChange={(e) => setDrumYears(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900/60 border border-white/10 rounded-xl text-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm"
                  required
                  disabled={loading}
                >
                  <option value="">-- Choisis ton niveau --</option>
                  <option value="1">🟢 1 an - Débutant</option>
                  <option value="2">🔵 2 ans - Intermédiaire</option>
                  <option value="3">🟡 3 ans - Confirmé</option>
                  <option value="4">🟠 4 ans - Avancé</option>
                  <option value="5">🔴 5+ ans - Expert</option>
                </select>
              </div>

              <div className="mb-3">
                <label htmlFor="profCode" className="block text-slate-300 font-semibold mb-1 text-sm">
                  Code professeur <span className="text-slate-500 font-normal">(optionnel)</span>
                </label>
                <input
                  type="text"
                  id="profCode"
                  value={profCode}
                  onChange={(e) => setProfCode(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900/60 border border-white/10 rounded-xl text-amber-50 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm"
                  placeholder="Ex : PROF-XXXXX"
                  disabled={loading}
                />
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2.5 rounded-full font-semibold text-white transition-all text-sm ${
              loading
                ? 'bg-slate-600 cursor-not-allowed'
                : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:shadow-lg'
            }`}
          >
            {loading ? 'Création du profil...' : isTeacher ? 'Créer mon profil professeur' : 'Créer mon profil'}
          </button>
        </form>

        <div className="mt-3 flex items-center gap-3">
          <img
            src="/images/marcel/MARCEL3.png"
            alt="Marcel vous souhaite la bienvenue"
            className="w-14 h-14 object-contain flex-shrink-0"
          />
          <p className="text-slate-400 italic text-sm">
            {isTeacher
              ? '"Bienvenue dans l\'équipe pédagogique !" - Marcel'
              : '"Bienvenue dans l\'aventure du rythme !" - Marcel'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginPage;
